'use client';
import React, { useState, useEffect } from 'react';

import { usePathname } from 'next/navigation';

const navLinks = [
  { href: '#home', label: 'Home', sectionId: 'home' },
  { href: '#about', label: 'About', sectionId: 'about' },
  { href: '#services', label: 'Services', sectionId: 'services' },
  { href: '#pricing', label: 'Pricing', sectionId: 'pricing' },
  { href: '#careers', label: 'Careers', sectionId: 'careers' },
  { href: '#contact', label: 'Contact', sectionId: 'contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const pathname = usePathname();

  const isHomePage = pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isHomePage) return;

    const sectionIds = navLinks.map((l) => l.sectionId);
    const observers: IntersectionObserver[] = [];

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    });

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    observers.push(observer);
    return () => observers.forEach((obs) => obs.disconnect());
  }, [isHomePage]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (isHomePage && href.startsWith('#')) {
      e.preventDefault();
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setActiveSection(id);
      }
      setMobileOpen(false);
    }
  };

  const isActive = (link: typeof navLinks[0]) => {
    if (isHomePage) return activeSection === link.sectionId;
    return pathname === link.href;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href={isHomePage ? '#home' : '/'}
            onClick={(e) => isHomePage && handleNavClick(e, '#home')}
            className="flex items-center gap-2.5 group"
            aria-label="QUANTIVA IT Home"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-cyan-DEFAULT flex items-center justify-center shadow-glow-sm group-hover:shadow-glow transition-all duration-300">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 2L15.5 5.5V12.5L9 16L2.5 12.5V5.5L9 2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M9 6L12 7.5V10.5L9 12L6 10.5V7.5L9 6Z" fill="white"/>
              </svg>
            </div>
            <span className={`font-bold text-lg tracking-tight transition-colors duration-200 ${
              scrolled ? 'text-navy-900' : 'text-white'
            }`}>
              QUANTIVA<span className="text-primary"> IT</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => (
              <a
                key={link?.href}
                href={isHomePage ? link.href : `/${link.href}`}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive(link)
                    ? 'text-primary bg-primary/10'
                    : scrolled
                    ? 'text-slate-600 hover:text-primary hover:bg-slate-50' :'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link?.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={isHomePage ? '#contact' : '/#contact'}
              onClick={(e) => isHomePage && handleNavClick(e, '#contact')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-full transition-all duration-300 hover:bg-primary-dark hover:shadow-glow hover:-translate-y-0.5"
            >
              Get Started
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 7H13M8 2L13 7L8 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors duration-200 ${
              scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M4 4L18 18M18 4L4 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M3 6H19M3 11H19M3 16H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 top-16 bg-navy-950/98 backdrop-blur-md transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col p-6 gap-2">
          {navLinks?.map((link) => (
            <a
              key={link?.href}
              href={isHomePage ? link.href : `/${link.href}`}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`px-4 py-3.5 text-base font-medium rounded-xl transition-all duration-200 ${
                isActive(link)
                  ? 'text-primary bg-primary/10' :'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {link?.label}
            </a>
          ))}
          <div className="mt-4 pt-4 border-t border-white/10">
            <a
              href={isHomePage ? '#contact' : '/#contact'}
              onClick={(e) => isHomePage && handleNavClick(e, '#contact')}
              className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-primary text-white font-semibold rounded-full transition-all duration-300 hover:bg-primary-dark"
            >
              Get Started
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 7H13M8 2L13 7L8 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
